import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useNotificationStore } from '../store';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import {
  Bell,
  UserPlus,
  Clock,
  AtSign,
  Clapperboard,
  Search,
  CalendarClock,
  PenLine,
  CalendarPlus,
  Calendar,
  AlertTriangle,
  Check,
  X,
} from 'lucide-react';

// Configuración visual por tipo de notificación (tipos del backend)
export const NOTIF_TYPE_CONFIG = {
  task_assigned: { icon: UserPlus, color: 'bg-purple-100 text-purple-600', label: 'Tarea asignada' },
  task_completed: { icon: Check, color: 'bg-emerald-100 text-emerald-600', label: 'Tarea completada' },
  task_due: { icon: Clock, color: 'bg-amber-100 text-amber-600', label: 'Vencimiento' },
  produce_content: { icon: Clapperboard, color: 'bg-blue-100 text-blue-600', label: 'Producción' },
  review_content: { icon: Search, color: 'bg-indigo-100 text-indigo-600', label: 'Revisión' },
  schedule_content: { icon: CalendarClock, color: 'bg-teal-100 text-teal-600', label: 'A programar' },
  request_changes: { icon: PenLine, color: 'bg-orange-100 text-orange-600', label: 'Cambios' },
  overdue_publication: { icon: AlertTriangle, color: 'bg-red-100 text-red-600', label: 'Atrasada' },
  mention: { icon: AtSign, color: 'bg-pink-100 text-pink-600', label: 'Mención' },
  meeting_invite: { icon: CalendarPlus, color: 'bg-emerald-100 text-emerald-600', label: 'Reunión' },
  meeting_today: { icon: Calendar, color: 'bg-emerald-100 text-emerald-600', label: 'Reunión hoy' },
  meeting: { icon: Calendar, color: 'bg-emerald-100 text-emerald-600', label: 'Reunión' },
  comment: { icon: AtSign, color: 'bg-blue-100 text-blue-600', label: 'Comentario' },
};

export const formatTimeAgo = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMins = Math.floor((now - date) / (1000 * 60));
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffMins < 1) return 'Ahora mismo';
  if (diffMins < 60) return `Hace ${diffMins} min`;
  if (diffHours < 24) return `Hace ${diffHours}h`;
  if (diffDays < 7) return `Hace ${diffDays}d`;
  return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
};

export function NotificationsDropdown() {
  const navigate = useNavigate();
  const { notifications, markAsRead, markAllAsRead, clearNotification } = useNotificationStore();
  const unreadCount = notifications.filter((n) => !n.read).length;

  const handleNotificationClick = (n) => {
    if (!n.read) markAsRead(n.id);
    if (n.link?.startsWith('http')) window.open(n.link, '_blank', 'noopener'); else if (n.link) navigate(n.link);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notifications-button">
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium"
              data-testid="notifications-unread-badge"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </motion.span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end" data-testid="notifications-dropdown">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold">Notificaciones</h3>
            {unreadCount > 0 && (
              <Badge variant="secondary" className="bg-red-100 text-red-700">
                {unreadCount} nuevas
              </Badge>
            )}
          </div>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={markAllAsRead}
              className="text-xs text-primary hover:text-primary"
              data-testid="mark-all-read-btn"
            >
              <Check className="w-3 h-3 mr-1" />
              Marcar todas
            </Button>
          )}
        </div>

        <ScrollArea className="h-[400px]">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Bell className="w-12 h-12 text-muted-foreground opacity-30 mb-3" />
              <p className="text-muted-foreground">No tienes notificaciones</p>
            </div>
          ) : (
            <div className="divide-y">
              <AnimatePresence>
                {notifications.map((n) => {
                  const cfg = NOTIF_TYPE_CONFIG[n.type] || NOTIF_TYPE_CONFIG.comment;
                  const Icon = cfg.icon;
                  return (
                    <motion.div
                      key={n.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      className={`group p-3 hover:bg-muted/50 cursor-pointer transition-colors ${!n.read ? 'bg-primary/5' : ''}`}
                      onClick={() => handleNotificationClick(n)}
                      data-testid={`notification-item-${n.id}`}
                    >
                      <div className="flex gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${cfg.color}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className={`text-sm font-medium ${!n.read ? 'text-foreground' : 'text-muted-foreground'}`}>
                                {n.title}
                              </p>
                              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                                {n.message}
                              </p>
                            </div>
                            {!n.read && <span className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-1.5" />}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className={`text-[10px] px-1.5 py-0 border-0 ${cfg.color}`}>
                              {cfg.label}
                            </Badge>
                            <span className="text-[10px] text-muted-foreground">{formatTimeAgo(n.created_at)}</span>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 opacity-0 group-hover:opacity-100 hover:bg-destructive/10 hover:text-destructive"
                          onClick={(e) => { e.stopPropagation(); clearNotification(n.id); }}
                          data-testid={`notification-delete-${n.id}`}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          )}
        </ScrollArea>

        {notifications.length > 0 && (
          <div className="p-2 border-t">
            <Button
              variant="ghost"
              className="w-full text-sm text-muted-foreground"
              onClick={() => navigate('/notifications')}
              data-testid="view-all-notifications-btn"
            >
              Ver todas las notificaciones
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
